/**
 * ConversationHistory — Sidebar panel showing past AI conversations
 * with search and ability to resume conversations.
 *
 * Supports two modes:
 * - inline: Persistent sidebar (like ChatGPT/Claude)
 * - overlay: Modal overlay (legacy)
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Search, MessageSquare, Clock, Trash2, Archive,
  ChevronLeft, Loader2, Plus, History,
  Hash, AlertCircle, PanelLeftClose, PanelLeft, Home,
} from 'lucide-react';
import { confirmDialog } from '../ConfirmDialog';
import apiClient from '../../lib/apiClient';
import './ConversationHistory.css';

function formatRelativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ConversationHistory({
  open,
  onClose,
  onSelectConversation,
  onNewConversation,
  onBack,
  currentSessionId,
  inline = false,
  collapsed = false,
  onToggleCollapse,
}) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Fetch conversations - always enabled in inline mode
  const { data: conversationsData, isLoading, error } = useQuery({
    queryKey: ['ai-conversations', showArchived],
    queryFn: () => apiClient.get(`/api/ai-conversations?limit=100&archived=${showArchived}`),
    enabled: inline || open,
    staleTime: 30_000,
  });

  const conversations = conversationsData?.conversations || [];

  // Filter conversations by search
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;

    const q = searchQuery.toLowerCase();
    return conversations.filter((conv) => {
      const title = (conv.title || '').toLowerCase();
      const summary = (conv.summary || '').toLowerCase();
      const topics = (conv.topics || []).join(' ').toLowerCase();
      return title.includes(q) || summary.includes(q) || topics.includes(q);
    });
  }, [conversations, searchQuery]);

  // Group conversations by date
  const groupedConversations = useMemo(() => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: [],
    };

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);
    const monthStart = new Date(todayStart);
    monthStart.setDate(monthStart.getDate() - 30);

    for (const conv of filteredConversations) {
      const date = new Date(conv.updated_at || conv.created_at);
      if (date >= todayStart) {
        groups.today.push(conv);
      } else if (date >= yesterdayStart) {
        groups.yesterday.push(conv);
      } else if (date >= weekStart) {
        groups.thisWeek.push(conv);
      } else if (date >= monthStart) {
        groups.thisMonth.push(conv);
      } else {
        groups.older.push(conv);
      }
    }

    return groups;
  }, [filteredConversations]);

  // Optimistic — drop the row from the visible list immediately.
  const removeFromList = (id) => {
    const key = ['ai-conversations', showArchived];
    const previous = queryClient.getQueryData(key);
    if (previous) {
      queryClient.setQueryData(key, {
        ...previous,
        conversations: (previous.conversations || []).filter((c) => c.id !== id),
      });
    }
    return { key, previous };
  };

  const rollback = (_err, _id, ctx) => {
    // Roll back optimistic update on failure.
    if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.del(`/api/ai-conversations/${id}`),
    onMutate: removeFromList,
    onError: rollback,
    onSettled: invalidate,
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: (id) => apiClient.put(`/api/ai-conversations/${id}/archive`),
    onMutate: removeFromList,
    onError: rollback,
    onSettled: invalidate,
  });

  const handleSelect = useCallback((conv) => {
    setSelectedId(conv.id);
    onSelectConversation?.(conv);
  }, [onSelectConversation]);

  const handleDelete = useCallback(async (e, id) => {
    e.stopPropagation();
    if (await confirmDialog('Delete this conversation? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }, [deleteMutation]);

  const handleArchive = useCallback((e, id) => {
    e.stopPropagation();
    archiveMutation.mutate(id);
  }, [archiveMutation]);

  const handleNewConversation = useCallback(() => {
    onNewConversation?.();
    onClose?.();
  }, [onNewConversation, onClose]);

  const renderConversationItem = (conv) => {
    const isActive = conv.id === selectedId || conv.session_id === currentSessionId;

    // Collapsed mode - just show icon
    if (inline && collapsed) {
      return (
        <button
          key={conv.id}
          className={`ch-conversation ch-conversation-collapsed ${isActive ? 'ch-conversation-active' : ''}`}
          onClick={() => handleSelect(conv)}
          title={conv.title || 'Untitled conversation'}
        >
          <MessageSquare size={16} />
        </button>
      );
    }

    return (
      <button
        key={conv.id}
        className={`ch-conversation ${isActive ? 'ch-conversation-active' : ''}`}
        onClick={() => handleSelect(conv)}
      >
        <div className="ch-conv-icon">
          <MessageSquare size={14} />
        </div>
        <div className="ch-conv-content">
          <div className="ch-conv-title">
            {conv.title || 'Untitled conversation'}
          </div>
          {conv.summary && (
            <div className="ch-conv-summary">{conv.summary}</div>
          )}
          <div className="ch-conv-meta">
            <span className="ch-conv-time">
              <Clock size={10} />
              {formatRelativeTime(conv.updated_at)}
            </span>
            {conv.message_count > 0 && (
              <span className="ch-conv-count">
                <Hash size={10} />
                {conv.message_count}
              </span>
            )}
          </div>
        </div>
        <div className="ch-conv-actions">
          <button
            className="ch-action-btn"
            onClick={(e) => handleArchive(e, conv.id)}
            title="Archive"
          >
            <Archive size={12} />
          </button>
          <button
            className="ch-action-btn ch-action-delete"
            onClick={(e) => handleDelete(e, conv.id)}
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </button>
    );
  };

  const renderGroup = (label, convs) => {
    if (convs.length === 0) return null;

    // In collapsed mode, don't show labels
    if (inline && collapsed) {
      return (
        <div className="ch-group ch-group-collapsed" key={label}>
          {convs.map(renderConversationItem)}
        </div>
      );
    }

    return (
      <div className="ch-group" key={label}>
        <div className="ch-group-label">{label}</div>
        {convs.map(renderConversationItem)}
      </div>
    );
  };

  // Inline sidebar mode
  if (inline) {
    return (
      <div className={`ch-sidebar ${collapsed ? 'ch-sidebar-collapsed' : ''}`}>
        {/* Header with back button and collapse toggle */}
        <div className="ch-sidebar-header">
          {onBack && (
            <button
              className="ch-back-btn"
              onClick={onBack}
              title="Back to main app"
            >
              <Home size={18} />
            </button>
          )}
          {!collapsed && (
            <div className="ch-header-title">
              <span>Sales AI</span>
            </div>
          )}
          <button
            className="ch-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        {/* New conversation button */}
        <div className="ch-sidebar-new">
          <button
            className={`ch-new-btn ${collapsed ? 'ch-new-btn-collapsed' : ''}`}
            onClick={handleNewConversation}
            title="New conversation"
          >
            <Plus size={16} />
            {!collapsed && <span>New chat</span>}
          </button>
        </div>

        {/* Search - hidden when collapsed */}
        {!collapsed && (
          <div className="ch-search">
            <Search size={14} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
            />
            {searchQuery && (
              <button className="ch-clear-search" onClick={() => setSearchQuery('')}>
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {/* Filters - hidden when collapsed */}
        {!collapsed && (
          <div className="ch-filters">
            <button
              className={`ch-filter-btn ${!showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(false)}
            >
              Recent
            </button>
            <button
              className={`ch-filter-btn ${showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(true)}
            >
              Archived
            </button>
          </div>
        )}

        {/* Conversation list */}
        <div className="ch-list">
          {isLoading ? (
            <div className={`ch-loading ${collapsed ? 'ch-loading-collapsed' : ''}`}>
              <Loader2 size={collapsed ? 16 : 20} className="ch-spinner" />
              {!collapsed && <span>Loading...</span>}
            </div>
          ) : error ? (
            <div className={`ch-error ${collapsed ? 'ch-error-collapsed' : ''}`}>
              <AlertCircle size={collapsed ? 16 : 20} />
              {!collapsed && <span>Failed to load</span>}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className={`ch-empty ${collapsed ? 'ch-empty-collapsed' : ''}`}>
              <MessageSquare size={collapsed ? 16 : 32} />
              {!collapsed && (
                <>
                  <h3>{searchQuery ? 'No matches' : 'No conversations'}</h3>
                  <p>{searchQuery ? 'Try different terms' : 'Start chatting'}</p>
                </>
              )}
            </div>
          ) : (
            <>
              {renderGroup('Today', groupedConversations.today)}
              {renderGroup('Yesterday', groupedConversations.yesterday)}
              {renderGroup('This Week', groupedConversations.thisWeek)}
              {renderGroup('This Month', groupedConversations.thisMonth)}
              {renderGroup('Older', groupedConversations.older)}
            </>
          )}
        </div>
      </div>
    );
  }

  // Overlay mode (legacy)
  if (!open) return null;

  return (
    <div className="ch-overlay" onClick={onClose}>
      <div className="ch-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ch-header">
          <div className="ch-header-title">
            <History size={18} />
            <h2>Conversation History</h2>
          </div>
          <button className="ch-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="ch-search">
          <Search size={14} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
          />
          {searchQuery && (
            <button className="ch-clear-search" onClick={() => setSearchQuery('')}>
              <X size={12} />
            </button>
          )}
        </div>

        <div className="ch-filters">
          <button
            className={`ch-filter-btn ${!showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(false)}
          >
            Recent
          </button>
          <button
            className={`ch-filter-btn ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(true)}
          >
            Archived
          </button>
        </div>

        <div className="ch-list">
          {isLoading ? (
            <div className="ch-loading">
              <Loader2 size={20} className="ch-spinner" />
              <span>Loading conversations...</span>
            </div>
          ) : error ? (
            <div className="ch-error">
              <AlertCircle size={20} />
              <span>Failed to load conversations</span>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="ch-empty">
              <MessageSquare size={32} />
              <h3>{searchQuery ? 'No matches found' : 'No conversations yet'}</h3>
              <p>
                {searchQuery
                  ? 'Try a different search term'
                  : 'Start a conversation to see it here'}
              </p>
            </div>
          ) : (
            <>
              {renderGroup('Today', groupedConversations.today)}
              {renderGroup('Yesterday', groupedConversations.yesterday)}
              {renderGroup('This Week', groupedConversations.thisWeek)}
              {renderGroup('This Month', groupedConversations.thisMonth)}
              {renderGroup('Older', groupedConversations.older)}
            </>
          )}
        </div>

        <div className="ch-footer">
          <button className="ch-new-btn" onClick={handleNewConversation}>
            <Plus size={14} />
            New Conversation
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConversationHistory;
